import React from 'react';

import { UserAvatar } from '@/components/UserAvatar';

import type { Agent } from '../data/types';

interface AgentRowHeaderProps {
  agent: Agent;
}

/**
 * Left-column row header for every grid row — including the synthetic
 * "Assigned to me" row, which renders identically (avatar with availability
 * dot + open / breached / at-risk counts) so the user's own bucket reads
 * consistently with their teammates.
 */
export function AgentRowHeader({ agent }: AgentRowHeaderProps) {
  return (
    <div className="flex items-start gap-2 py-3 pl-3 pr-2 h-full">
      <UserAvatar
        size="md"
        name={agent.avatarName ?? agent.name}
        email={agent.avatarEmail}
        userId={agent.avatarUserId ?? agent.id}
        available={agent.online}
        aria-label={agent.online ? 'Online' : 'Offline'}
      />
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-foreground truncate">{agent.name}</span>
        <div className="flex flex-col gap-0.5 text-[11px] text-muted-foreground mt-1 leading-tight">
          <span>
            <span className="font-semibold text-foreground tabular-nums">{agent.openCount}</span>{' '}
            open
          </span>
          {agent.breachedCount > 0 && (
            <span>
              <span className="font-semibold text-critical-foreground tabular-nums">
                {agent.breachedCount}
              </span>{' '}
              breached
            </span>
          )}
          {agent.atRiskCount > 0 && (
            <span>
              <span className="font-semibold text-attention-foreground tabular-nums">
                {agent.atRiskCount}
              </span>{' '}
              at risk
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
